<template>
  <div>
    <NavBar>
      <template #left>
        <ButtonIconContextual
          :icon="computedIcon"
          label="Voltar"
          @click="onBack"
        />
      </template>
      <template #center>Performance</template>
      <template #right>
        <ShowMoneyToggleButton />
      </template>
    </NavBar>
    <main class="wealth-performance__content">
      <SpinnerLoader
        v-if="isLoading"
        data-testid="wealth-performance-loader"
        :size="48"
      />
      <HelperViewStatus
        v-else-if="hasError"
        generic-error
        button-label="Tentar novamente"
        @click="getWealthPerformance"
      />
      <HelperViewContextual
        v-else-if="isEmpty"
        icon="ED0030"
        description="Ainda não existem dados de Rendimento bruto e Rentabilidade bruta para este período."
      />

      <div v-else class="wealth-performance__accordion">
        <WealthPerformanceAccordions
          :portfolio-id="portfolioId"
          :history-overview="historyOverview"
          :updated-at="updatedAt"
        />
      </div>
    </main>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router/composables';
import {
  NavBar,
  ButtonIconContextual,
  SpinnerLoader,
  HelperViewContextual,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { ShowMoneyToggleButton } from '@/modules/common/components/buttons/ShowMoneyToggleButton';
import { WealthPerformanceAccordions } from './components/WealthPerformanceAccordions';

import {
  getPerformanceHistoryOverview,
  IDefaultProps
} from '@/modules/wealth/services/portfolio';
import {
  getPerformance,
  getPortfoliosPerformance
} from '@/modules/wealth/services/investment';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';

const router = useRouter();
const route = useRoute();

const hasError = ref(false);
const isLoading = ref(false);
const historyOverview = ref<IDefaultProps[]>([]);
const updatedAt = ref('');
const portfolioId = ref('');

const isEmpty = computed(() => historyOverview.value.length === 0);
const computedIcon = computed(() => {
  return route.query.portfolio ? 'EA0030' : 'EF0030';
});

const isEnabledBffInvestmentsMigration = computed(() => {
  return FeatureFlagsModule.isEnabled(
    FeatureFlags.ENABLE_BFF_INVESTMENTS_MIGRATION
  );
});

async function getWealthPerformance() {
  if (route.query.portfolio) {
    portfolioId.value = route.query.portfolio as string;
    await fetchPortfolioPerformance();
    return;
  }
  await fetchWealthPerformance();
}

async function fetchPortfolioPerformance() {
  try {
    hasError.value = false;
    isLoading.value = true;

    const data = await getPerformanceHistoryOverview(portfolioId.value);
    historyOverview.value = data.performanceHistoryOverview;
    updatedAt.value = data.updatedAt.formatted;
  } catch (error) {
    console.error(error);
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function fetchWealthPerformance() {
  try {
    hasError.value = false;
    isLoading.value = true;

    const data = isEnabledBffInvestmentsMigration.value
      ? await getPortfoliosPerformance()
      : await getPerformance();
    historyOverview.value = data.performanceHistoryOverview;
    updatedAt.value = data.updatedAt.formatted;
  } catch (error) {
    console.error(error);
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

function onBack() {
  router.back();
}

onMounted(() => {
  getWealthPerformance();
});
</script>

<style lang="less" scoped>
.wealth-performance {
  &__content {
    min-height: calc(100vh - 64px);
    display: flex;
    align-items: center;
    justify-content: center;

    .helper-view {
      max-width: 328px;
    }
  }

  &__accordion {
    align-self: flex-start;
    width: 100%;
  }
}
</style>
